import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { TranslateService } from '@ngx-translate/core';
import { FullUser } from '../../models/full-user.model';
import {
  EmployeeClothingOverview,
  ClothingBiometry,
  EmployeeClothingEntitlementsSource,
  ClothingProfileStorekeeper,
  ClothingLossCasePayload,
  ClothingRequestPayload,
  ClothingRequest,
  StorekeeperIssuePayload,
  StorekeeperRejectNoStockPayload
} from '../../models/clothing.model';
import { ProfileClothingUnitItem, ProfileClothingViewItem } from '../../models/profile-clothing.model';
import { User } from '../../models/user.model';
import { ClothingService } from '../../services/clothing.service';
import { ClothingErrorService } from '../../services/clothing-error.service';
import { ProfileClothingViewService } from '../../services/profile-clothing-view.service';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';
import { ClothingIssueModalComponent } from '../../modals/clothing-issue-modal.component';
import { ClothingLossModalComponent } from '../../modals/clothing-loss-modal.component';

const BIOMETRY_MAX_VALUE = 999;
const BIOMETRY_MIN_VALUE = 1;
const BIOMETRY_SUCCESS_KEY = 'SUC';
const CLOTHING_REPEAT_EMPTY = '';
const CLOTHING_REPEAT_SUCCESS = 'CLOTHING_REQUEST_REPEAT_SUCCESS';
const CLOTHING_REPEAT_UNAVAILABLE = 'CLOTHING_REQUEST_REPEAT_UNAVAILABLE';
const CLOTHING_REPEAT_ERROR = 'CLOTHING_REQUEST_REPEAT_ERROR';
const CLOTHING_ISSUED_STATUS = 'ISSUED';
const CLOTHING_REJECTED_STATUS = 'REJECTED_NO_STOCK';
const BIOMETRY_FIELDS = [
  {key: 'growth', label: 'PROFILE_BIOMETRY_GROWTH'},
  {key: 'weight', label: 'PROFILE_BIOMETRY_WEIGHT'},
  {key: 'clothingSize', label: 'CLOTHING_BIOMETRY_SIZE'},
  {key: 'shoeSize', label: 'CLOTHING_BIOMETRY_SHOE'}
];

@Component({
  selector: 'app-my-profile',
  templateUrl: './my-profile.component.html',
  styleUrls: ['./my-profile.component.css']
})
export class MyProfileComponent implements OnInit, OnDestroy {
  private user: User;
  public userAPI: FullUser;
  public isLoaded = false;
  public errorInfo = false;
  public isAdmin = false;
  public isAdmins = false;
  public isUser = false;
  public isHead = false;
  public isStorekeeper = false;
  public showEmployeeClothing = false;
  public showStorekeeperClothing = false;
  public showClothingPanel = false;
  public learningSumWeight: number = 0;
  public isLearningPointsLoaded = false;
  public learningPointsError = false;
  public isClothingLoaded = false;
  public clothingError = false;
  public clothingItems: ProfileClothingViewItem[] = [];
  public clothingStorekeeper: ClothingProfileStorekeeper;
  public readonly biometryFields = BIOMETRY_FIELDS;
  public readonly biometryMaxValue = BIOMETRY_MAX_VALUE;
  public biometryDraft: ClothingBiometry = null;
  public biometrySaving = false;
  public biometryError = false;
  private readonly biometrySubscriptions = new Subscription();
  private readonly clothingRepeatSubscriptions = new Subscription();
  public repeatRequestId = CLOTHING_REPEAT_EMPTY;

  private requestCatalogItemId = '';
  private lossCatalogItemId = '';
  private issueActionKey = '';
  private rejectRequestId = '';
  private expandedClothingCatalogItemId = '';

  constructor(
    private userService: UserService,
    private clothingService: ClothingService,
    private clothingErrorService: ClothingErrorService,
    private profileClothingViewService: ProfileClothingViewService,
    private toastService: ToastService,
    private translate: TranslateService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.user = this.userService.getLocal();
    this.setRoleFlags();
    this.loadProfile();
    this.loadLearningPoints();
    this.loadClothing();
  }

  ngOnDestroy(): void {
    this.biometrySubscriptions.unsubscribe();
    this.clothingRepeatSubscriptions.unsubscribe();
  }

  editBiometry(): void {
    if (!this.userAPI || this.biometrySaving) {
      return;
    }
    this.biometryDraft = Object.assign({}, this.userAPI.parametersUser);
    this.biometryError = false;
  }

  cancelBiometry(): void {
    if (!this.biometrySaving) {
      this.biometryDraft = null;
      this.biometryError = false;
    }
  }

  get biometryValid(): boolean {
    return !!this.biometryDraft && this.biometryFields.every(field => {
      const value = this.biometryDraft[field.key];
      return Number.isInteger(value) && value >= BIOMETRY_MIN_VALUE && value <= BIOMETRY_MAX_VALUE;
    });
  }

  trackByBiometryField(index: number, field: {key: string}): string {
    return field.key;
  }

  saveBiometry(): void {
    if (this.biometrySaving || !this.biometryValid) {
      return;
    }
    const saved = Object.assign({}, this.biometryDraft);
    this.biometrySaving = true;
    this.biometryError = false;
    this.biometrySubscriptions.add(this.userService.saveMyBiometry(
      saved.growth, saved.weight, saved.clothingSize, saved.shoeSize
    ).subscribe(() => this.handleBiometrySaved(saved), () => {
      this.biometrySaving = false;
      this.biometryError = true;
    }));
  }

  private handleBiometrySaved(saved: ClothingBiometry): void {
    this.userAPI.parametersUser = saved;
    this.biometrySaving = false;
    this.cancelBiometry();
    this.toastService.success(this.translate.instant(BIOMETRY_SUCCESS_KEY));
  }

  trackByClothingItem(index: number, item: ProfileClothingViewItem): string {
    return item.catalogItemId;
  }

  trackByClothingUnit(index: number, unit: ProfileClothingUnitItem): string {
    if (unit && unit.unitId) {
      return unit.unitId;
    }
    return String(index);
  }

  trackByRole(index: number, role: string): string {
    return role;
  }

  showMobileProfile(): boolean {
    return !!this.userAPI && !this.errorInfo;
  }

  getMobileProfileInitials(): string {
    if (!this.userAPI) {
      return '';
    }
    var lastInitial = this.userAPI.lastName ? this.userAPI.lastName.charAt(0) : '';
    var firstInitial = this.userAPI.firstName ? this.userAPI.firstName.charAt(0) : '';
    var initials = (lastInitial + firstInitial).toUpperCase();
    return initials || '?';
  }

  getMobileScheduleTime(): string {
    if (!this.userAPI || !this.userAPI.schedule || !this.userAPI.schedule.schedule) {
      return '';
    }

    var schedule = this.userAPI.schedule.schedule;
    var workStart = schedule.workStart || '';
    var workEnd = schedule.workEnd || '';

    if (!workStart && !workEnd) {
      return '';
    }

    if (!workStart) {
      return workEnd;
    }

    if (!workEnd) {
      return workStart;
    }

    return workStart + ' - ' + workEnd;
  }

  isRequestPending(item: ProfileClothingViewItem): boolean {
    return this.requestCatalogItemId === item.catalogItemId;
  }

  isLossPending(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): boolean {
    return this.lossCatalogItemId === this.createLossActionKey(item, unit);
  }

  isIssuePending(item: ProfileClothingViewItem): boolean {
    return this.issueActionKey === this.createIssueActionKey(item);
  }

  isRejectPending(item: ProfileClothingViewItem): boolean {
    return !!item.requestId && this.rejectRequestId === item.requestId;
  }

  hasClothingUnits(item: ProfileClothingViewItem): boolean {
    return !!item && !!item.units && !!item.units.length;
  }

  toggleClothingItem(item: ProfileClothingViewItem): void {
    if (!this.hasClothingUnits(item)) {
      return;
    }
    if (this.isClothingItemExpanded(item)) {
      this.expandedClothingCatalogItemId = '';
      return;
    }
    this.expandedClothingCatalogItemId = item.catalogItemId;
  }

  isClothingItemExpanded(item: ProfileClothingViewItem): boolean {
    return !!item && this.expandedClothingCatalogItemId === item.catalogItemId;
  }

  getUnitLifeWidth(unit: ProfileClothingUnitItem): string {
    return unit.lifePercent + '%';
  }

  getUnitStatusKey(unit: ProfileClothingUnitItem): string {
    if (!unit || !unit.status) {
      return 'NO_DATA';
    }
    return 'CLOTHING_UNIT_STATUS_' + unit.status;
  }

  getClothingHintKey(): string {
    if (this.showStorekeeperClothing) {
      return 'CLOTHING_STOREKEEPER_PROFILE_HINT';
    }
    return 'CLOTHING_EMPLOYEE_PROFILE_HINT';
  }

  showStorekeeperContact(): boolean {
    return !!this.clothingStorekeeper && !!this.clothingStorekeeper.userId;
  }

  hasStorekeeperActions(item: ProfileClothingViewItem): boolean {
    return this.showIssueAction(item) || this.showRejectAction(item);
  }

  showIssueAction(item: ProfileClothingViewItem): boolean {
    return this.showStorekeeperClothing && !!item && item.canIssue;
  }

  showRejectAction(item: ProfileClothingViewItem): boolean {
    return this.showStorekeeperClothing && !this.isAdmin && !!item && item.hasActiveRequest;
  }

  showSelfStorekeeperIssueAction(): boolean {
    return this.showEmployeeClothing && this.isStorekeeper;
  }

  showEmployeeRequestAction(): boolean {
    if (!this.showEmployeeClothing) {
      return false;
    }
    return !this.isStorekeeper;
  }

  isSelfStorekeeperIssueDisabled(item: ProfileClothingViewItem): boolean {
    return item.issueDisabled || this.isIssuePending(item);
  }

  createEmployeeRequest(item: ProfileClothingViewItem): void {
    if (item.requestDisabled || this.isRequestPending(item)) {
      return;
    }
    this.requestCatalogItemId = item.catalogItemId;
    this.clothingService.createEmployeeRequest(this.createRequestPayload(item)).subscribe(
      () => this.handleRequestSuccess(),
      (error) => this.handleRequestError(error)
    );
  }

  repeatEmployeeRequest(item: ProfileClothingViewItem): void {
    if (!item.canRepeatRequest || !item.requestId || this.repeatRequestId) {
      return;
    }
    this.repeatRequestId = item.requestId;
    this.clothingRepeatSubscriptions.add(this.clothingService.repeatEmployeeRequest(item.requestId).subscribe(
      response => this.handleRepeatResponse(response),
      error => this.handleRepeatError(error)
    ));
  }

  private handleRepeatResponse(response: ClothingRequest): void {
    this.repeatRequestId = CLOTHING_REPEAT_EMPTY;
    if (response.notificationResent) {
      this.toastService.success(this.translate.instant(CLOTHING_REPEAT_SUCCESS));
    } else {
      this.toastService.info(this.translate.instant(CLOTHING_REPEAT_UNAVAILABLE));
    }
    this.loadClothing();
  }

  private handleRepeatError(error: HttpErrorResponse): void {
    this.repeatRequestId = CLOTHING_REPEAT_EMPTY;
    this.toastService.error(this.resolveErrorMessage(error, CLOTHING_REPEAT_ERROR));
    this.loadClothing();
  }

  createEmployeeLossCase(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): void {
    if (this.isLossDisabled(item, unit) || this.isLossPending(item, unit)) {
      return;
    }
    if (this.shouldCreateRequestForHeadLoss()) {
      this.createHeadLossRequest(item, unit);
      return;
    }
    if (!unit || !unit.unitId) {
      return;
    }
    this.openLossModal(item, unit);
  }

  issueStorekeeperItem(item: ProfileClothingViewItem): void {
    if (this.showSelfStorekeeperIssueAction() && this.isSelfStorekeeperIssueDisabled(item)) {
      return;
    }
    if (item.issueDisabled || this.isIssuePending(item)) {
      return;
    }
    this.openIssueModal(item);
  }

  rejectStorekeeperRequest(item: ProfileClothingViewItem): void {
    if (item.rejectDisabled || this.isRejectPending(item)) {
      return;
    }
    this.rejectRequestId = item.requestId;
    this.clothingService.rejectStorekeeperRequest(
      item.requestId,
      this.createRejectPayload()
    ).subscribe(
      (response) => this.handleRejectResponse(response),
      (error) => this.handleRejectError(error)
    );
  }

  getLifeWidth(item: ProfileClothingViewItem): string {
    return item.lifePercent + '%';
  }

  private isLossDisabled(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): boolean {
    if (unit) {
      return unit.lossDisabled;
    }
    return item.lossDisabled;
  }

  private setRoleFlags(): void {
    this.isAdmins = this.hasRole('ROLE_ADMIN');
    this.isHead = this.hasRole('ROLE_HEAD');
    this.isUser = this.hasRole('ROLE_USER');
    this.isStorekeeper = this.hasStorekeeperRole();
    this.showStorekeeperClothing = false;
    this.showEmployeeClothing = this.canShowOwnEmployeeClothing();
    this.showClothingPanel = this.showEmployeeClothing || this.showStorekeeperClothing;
  }

  private hasRole(role: string): boolean {
    if (!this.user || !this.user.roles) {
      return false;
    }
    return this.user.roles.indexOf(role) !== -1;
  }

  private hasStorekeeperRole(): boolean {
    return this.hasRole('ROLE_STOREKEEPER') || this.hasRole('STOREKEEPER');
  }

  private canShowOwnEmployeeClothing(): boolean {
    return this.isUser || this.isHead || this.isAdmins || this.isStorekeeper;
  }

  private shouldCreateRequestForHeadLoss(): boolean {
    return this.isHead;
  }

  private loadProfile(): void {
    this.userService.getUserFromAPI(this.user.id).subscribe(
      (response) => this.handleProfileResponse(response.body as FullUser),
      () => this.handleProfileError()
    );
  }

  private handleProfileResponse(data: FullUser): void {
    this.userAPI = data;
    this.isAdmin = this.hasApiRole('ADMIN', data);
    this.isLoaded = true;
    this.errorInfo = false;
  }

  private hasApiRole(role: string, profile: FullUser): boolean {
    if (!profile || !profile.roles) {
      return false;
    }
    return profile.roles.indexOf(role) !== -1;
  }

  private handleProfileError(): void {
    this.isLoaded = true;
    this.errorInfo = true;
  }

  private loadLearningPoints(): void {
    this.isLearningPointsLoaded = false;
    this.learningPointsError = false;
    this.userService.getOwnLearningWeightSum().subscribe(
      (response) => {
        const body: any = response.body || {};
        this.learningSumWeight = typeof body.sumWeight === 'number' ? body.sumWeight : 0;
        this.isLearningPointsLoaded = true;
      },
      () => {
        this.learningSumWeight = 0;
        this.isLearningPointsLoaded = true;
        this.learningPointsError = true;
      }
    );
  }

  reloadClothing(): void { this.loadClothing(); }

  private loadClothing(): void {
    if (!this.showClothingPanel) {
      return;
    }
    if (this.isStorekeeper) {
      this.loadStorekeeperClothing();
      return;
    }
    this.loadEmployeeClothing();
  }

  private loadEmployeeClothing(): void {
    this.isClothingLoaded = false;
    this.clothingError = false;
    Observable.forkJoin(
      this.loadEmployeeOverviewSafely(),
      this.clothingService.getEmployeeEntitlements()
    ).subscribe(
      (response) => this.handleEmployeeClothingSuccess(response),
      () => this.handleClothingError()
    );
  }

  private loadEmployeeOverviewSafely(): Observable<EmployeeClothingOverview> {
    return this.clothingService.getEmployeeOverview()
      .catch(() => Observable.of(null));
  }

  private loadStorekeeperClothing(): void {
    this.isClothingLoaded = false;
    this.clothingError = false;
    Observable.forkJoin(
      this.clothingService.getStorekeeperEmployeeOverview(this.user.id),
      this.clothingService.getStorekeeperEmployeeEntitlements(this.user.id)
    ).subscribe(
      (response) => this.handleStorekeeperClothingSuccess(response),
      () => this.handleClothingError()
    );
  }

  private handleEmployeeClothingSuccess(response: Object[]): void {
    var overview = response[0] as EmployeeClothingOverview;
    var items = this.profileClothingViewService.mapEmployeeOverview(
      overview,
      response[1] as EmployeeClothingEntitlementsSource
    );
    this.handleClothingSuccess(overview, items);
  }

  private handleStorekeeperClothingSuccess(response: Object[]): void {
    var overview = response[0] as EmployeeClothingOverview;
    var items = this.profileClothingViewService.mapSelfStorekeeperOverview(
      overview,
      response[1] as EmployeeClothingEntitlementsSource
    );
    this.handleClothingSuccess(overview, items);
  }

  private handleClothingSuccess(
    overview: EmployeeClothingOverview,
    items: ProfileClothingViewItem[]
  ): void {
    this.clothingItems = items;
    this.clothingStorekeeper = this.resolveClothingStorekeeper(overview);
    this.isClothingLoaded = true;
    this.clothingError = false;
    this.showClothingPanel = this.hasClothingItems(items);
    this.expandedClothingCatalogItemId = '';
    this.clearPendingActions();
  }

  private handleClothingError(): void {
    this.clothingItems = [];
    this.clothingStorekeeper = null;
    this.isClothingLoaded = true;
    this.clothingError = true;
    this.showClothingPanel = this.showEmployeeClothing || this.showStorekeeperClothing;
    this.clearPendingActions();
  }

  private hasClothingItems(items: ProfileClothingViewItem[]): boolean {
    return !!items && !!items.length;
  }

  private resolveClothingStorekeeper(
    overview: EmployeeClothingOverview
  ): ClothingProfileStorekeeper {
    if (!overview || !overview.storekeeper || !overview.storekeeper.userId) {
      return null;
    }
    return overview.storekeeper;
  }

  private createRequestPayload(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): ClothingRequestPayload {
    var payload: ClothingRequestPayload = {
      catalogItemId: item.catalogItemId,
      requestedQuantity: 1
    };
    if (unit && unit.unitId) {
      payload.unitId = unit.unitId;
    }
    return payload;
  }

  private createLossPayload(
    item: ProfileClothingViewItem,
    description: string,
    unit: ProfileClothingUnitItem
  ): ClothingLossCasePayload {
    return {
      catalogItemId: item.catalogItemId,
      unitId: unit.unitId,
      description: description
    };
  }

  private createRejectPayload(): StorekeeperRejectNoStockPayload {
    return {
      description: ''
    };
  }

  private createHeadLossRequest(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): void {
    this.lossCatalogItemId = this.createLossActionKey(item, unit);
    this.clothingService.createEmployeeRequest(this.createRequestPayload(item, unit)).subscribe(
      () => this.handleHeadLossRequestSuccess(),
      (error) => this.handleHeadLossRequestError(error)
    );
  }

  private handleRequestSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_REQUEST_CREATED'));
    this.loadClothing();
  }

  private handleLossSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_LOSS_CREATED'));
    this.loadClothing();
  }

  private handleHeadLossRequestSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_REQUEST_CREATED'));
    this.loadClothing();
  }

  private handleIssueSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_ISSUE_SUCCESS'));
    this.loadClothing();
  }

  private handleIssueResponse(response: ClothingRequest): void {
    if (response.status === CLOTHING_ISSUED_STATUS) {
      this.handleIssueSuccess();
      return;
    }
    this.loadClothing();
  }

  private handleRejectResponse(response: ClothingRequest): void {
    if (response.status === CLOTHING_REJECTED_STATUS) {
      this.handleRejectSuccess();
      return;
    }
    this.loadClothing();
  }

  private handleRejectSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_REJECT_SUCCESS'));
    this.loadClothing();
  }

  private handleRequestError(error: HttpErrorResponse): void {
    this.requestCatalogItemId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_REQUEST_CREATE_ERROR'));
  }

  private handleLossError(error: HttpErrorResponse): void {
    this.lossCatalogItemId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_LOSS_CREATE_ERROR'));
  }

  private handleHeadLossRequestError(error: HttpErrorResponse): void {
    this.lossCatalogItemId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_REQUEST_CREATE_ERROR'));
  }

  private handleIssueError(error: HttpErrorResponse): void {
    this.issueActionKey = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_ISSUE_ERROR'));
  }

  private handleRejectError(error: HttpErrorResponse): void {
    this.rejectRequestId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_REJECT_ERROR'));
  }

  private resolveErrorMessage(error: HttpErrorResponse, fallbackKey: string): string {
    return this.clothingErrorService.resolveMessage(error, fallbackKey);
  }

  private clearPendingActions(): void {
    this.requestCatalogItemId = '';
    this.lossCatalogItemId = '';
    this.issueActionKey = '';
    this.rejectRequestId = '';
  }

  private issueStorekeeperItemByMode(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    if (item.issueByRequest) {
      this.issueStorekeeperRequest(item, payload);
      return;
    }
    this.directIssueStorekeeperItem(item, payload);
  }

  private issueStorekeeperRequest(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    this.clothingService.issueStorekeeperRequest(
      item.requestId,
      payload
    ).subscribe(
      (response) => this.handleIssueResponse(response),
      (error) => this.handleIssueError(error)
    );
  }

  private directIssueStorekeeperItem(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    this.clothingService.directIssueStorekeeperItem(
      this.user.id,
      item.catalogItemId,
      payload
    ).subscribe(
      () => this.handleIssueSuccess(),
      (error) => this.handleIssueError(error)
    );
  }

  private createIssueActionKey(item: ProfileClothingViewItem): string {
    if (item.requestId) {
      return item.requestId;
    }
    return item.catalogItemId;
  }

  private createLossActionKey(item: ProfileClothingViewItem, unit?: ProfileClothingUnitItem): string {
    if (unit && unit.unitId) {
      return unit.unitId;
    }
    return item.catalogItemId;
  }

  private openIssueModal(item: ProfileClothingViewItem): void {
    var modalRef = this.modalService.open(ClothingIssueModalComponent);
    modalRef.componentInstance.setValues(item.name, item.issueQuantity, this.getIssueEmployeeName());
    modalRef.result.then(
      (payload) => this.submitStorekeeperIssue(item, payload),
      () => null
    );
  }

  private openLossModal(item: ProfileClothingViewItem, unit: ProfileClothingUnitItem): void {
    var modalRef = this.modalService.open(ClothingLossModalComponent);

    modalRef.componentInstance.setValues(item.name, '', unit ? unit.unitNumber : 0);
    modalRef.result.then(
      (description) => this.submitEmployeeLossCase(item, description, unit),
      () => null
    );
  }

  private submitEmployeeLossCase(
    item: ProfileClothingViewItem,
    description: string,
    unit: ProfileClothingUnitItem
  ): void {
    if (!description) {
      return;
    }
    this.lossCatalogItemId = this.createLossActionKey(item, unit);
    this.clothingService.createEmployeeLossCase(
      this.createLossPayload(item, description, unit)
    ).subscribe(
      () => this.handleLossSuccess(),
      (error) => this.handleLossError(error)
    );
  }

  private submitStorekeeperIssue(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    if (!payload) {
      return;
    }
    this.issueActionKey = this.createIssueActionKey(item);
    this.issueStorekeeperItemByMode(item, payload);
  }

  private getIssueEmployeeName(): string {
    if (!this.userAPI) {
      return '';
    }
    return [
      this.userAPI.lastName,
      this.userAPI.firstName,
      this.userAPI.patronymicName
    ].filter((value) => !!value).join(' ');
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-profile/my-profile.component.ts